import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  InputAdornment,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  DirectionsCar as VehicleIcon,
  Event as EventIcon,
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckCircleIcon,
  Pending as PendingIcon,
  AttachMoney as MoneyIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { eventService, vehicleService, eventVehicleUsageService, expenseService, fuelPriceService } from '../services/api';
import { Event, Vehicle, EventVehicleUsage, FuelPrice } from '../types';
import { divertilandiaColors } from '../theme/divertilandiaColors';

// Interfaz para el desglose de costos híbridos
interface HybridCostBreakdown {
  electricKm: number;
  electricKwh: number;
  electricCost: number;
  fuelKm: number;
  fuelLiters: number;
  fuelCost: number;
  totalCost: number;
  exceedsElectricRange: boolean;
}

// Componente para mostrar el resumen del costo de combustible
const FuelCostSummary: React.FC<{
  vehicleId: string;
  kilometers: number;
  initialBatteryPercentage?: number | null;
  vehicles: Vehicle[];
  formatPrice: (price: number) => string;
}> = ({ vehicleId, kilometers, initialBatteryPercentage, vehicles, formatPrice }) => {
  const [estimatedCost, setEstimatedCost] = useState<number>(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fuelPrices, setFuelPrices] = useState<FuelPrice[]>([]);
  const [hybridBreakdown, setHybridBreakdown] = useState<HybridCostBreakdown | null>(null);

  const calculateCost = useCallback(async (initialBatteryPercentage?: number | null) => {
    try {
      setLoading(true);
      setError(null);
      
      const vehicle = vehicles.find(v => v.id === vehicleId);
      if (!vehicle) {
        setError('Vehículo no encontrado');
        return;
      }

      // Obtener precios de combustible y electricidad
      const prices = await fuelPriceService.getCurrentPrices();
      setFuelPrices(prices);

      // Si es vehículo híbrido, calcular desglose
      if (vehicle.vehicle_type === 'hybrid' && vehicle.electric_range_km && vehicle.battery_capacity_kwh && vehicle.hybrid_efficiency_km_per_liter) {
        const electricityPrice = prices.find(p => p.fuel_type === 'electricity')?.price_per_liter || 0;
        const fuelPrice = prices.find(p => p.fuel_type === vehicle.fuel_type)?.price_per_liter || 0;
        const batteryMinPercentage = vehicle.battery_min_percentage || 25;
        
        // Si no se especifica el porcentaje inicial, asumir 100%
        const initialBattery = initialBatteryPercentage !== null && initialBatteryPercentage !== undefined ? initialBatteryPercentage : 100;
        
        // Si el porcentaje inicial es menor o igual al mínimo, no se puede usar batería
        if (initialBattery <= batteryMinPercentage) {
          // Solo usar gasolina
          const litersNeeded = kilometers / vehicle.hybrid_efficiency_km_per_liter;
          const fuelCost = litersNeeded * fuelPrice;
          
          const breakdown: HybridCostBreakdown = {
            electricKm: 0,
            electricKwh: 0,
            electricCost: 0,
            fuelKm: kilometers,
            fuelLiters: litersNeeded,
            fuelCost: fuelCost,
            totalCost: fuelCost,
            exceedsElectricRange: true
          };
          
          setHybridBreakdown(breakdown);
          setEstimatedCost(breakdown.totalCost);
          return;
        }
        
        // Calcular el porcentaje de batería disponible (desde el inicial hasta el mínimo)
        const availableBatteryPercentage = initialBattery - batteryMinPercentage;
        
        // Calcular el rango eléctrico disponible basado en el porcentaje inicial
        const availableElectricRange = vehicle.electric_range_km * (availableBatteryPercentage / 100);
        
        let breakdown: HybridCostBreakdown;
        
        // Si los kilómetros están dentro del rango eléctrico disponible
        if (kilometers <= availableElectricRange) {
          // Solo electricidad
          const batteryPercentageUsed = (kilometers / vehicle.electric_range_km) * 100;
          const kwhConsumed = (batteryPercentageUsed / 100) * vehicle.battery_capacity_kwh;
          const electricCost = kwhConsumed * electricityPrice;
          
          breakdown = {
            electricKm: kilometers,
            electricKwh: kwhConsumed,
            electricCost: electricCost,
            fuelKm: 0,
            fuelLiters: 0,
            fuelCost: 0,
            totalCost: electricCost,
            exceedsElectricRange: false
          };
        } else {
          // Parte eléctrica (hasta el límite disponible)
          const electricKm = availableElectricRange;
          const batteryPercentageUsed = (electricKm / vehicle.electric_range_km) * 100;
          const kwhConsumed = (batteryPercentageUsed / 100) * vehicle.battery_capacity_kwh;
          const electricCost = kwhConsumed * electricityPrice;
          
          // Parte con gasolina (kilómetros restantes)
          const fuelKm = kilometers - availableElectricRange;
          const litersNeeded = fuelKm / vehicle.hybrid_efficiency_km_per_liter;
          const fuelCost = litersNeeded * fuelPrice;
          
          breakdown = {
            electricKm: electricKm,
            electricKwh: kwhConsumed,
            electricCost: electricCost,
            fuelKm: fuelKm,
            fuelLiters: litersNeeded,
            fuelCost: fuelCost,
            totalCost: electricCost + fuelCost,
            exceedsElectricRange: true
          };
        }
        
        setHybridBreakdown(breakdown);
        setEstimatedCost(breakdown.totalCost);
      } else {
        // Vehículo regular - usar el endpoint del backend
        const response = await vehicleService.calculateFuelCost(vehicleId, kilometers);
        const fuelCost = response.data?.fuel_cost || response.fuel_cost || 0;
        setEstimatedCost(fuelCost);
        setHybridBreakdown(null);
      }
    } catch (err) {
      console.error('Error calculating fuel cost:', err);
      setError('Error al calcular el costo');
      setEstimatedCost(0);
      setHybridBreakdown(null);
    } finally {
      setLoading(false);
    }
  }, [vehicleId, kilometers, vehicles]);

  useEffect(() => {
    if (vehicleId && kilometers > 0) {
      calculateCost(initialBatteryPercentage);
    }
  }, [vehicleId, kilometers, initialBatteryPercentage, calculateCost]);

  const vehicle = vehicles.find(v => v.id === vehicleId);
  if (!vehicle) return null;

  const isHybrid = vehicle.vehicle_type === 'hybrid';
  const electricityPrice = fuelPrices.find(p => p.fuel_type === 'electricity')?.price_per_liter || 0;
  const fuelPrice = fuelPrices.find(p => p.fuel_type === vehicle.fuel_type)?.price_per_liter || 0;

  return (
    <>
      <Typography variant="body1" sx={{ color: 'text.secondary' }}>
        <strong>Vehículo:</strong> {vehicle.name}
      </Typography>
      <Typography variant="body1" sx={{ color: 'text.secondary' }}>
        <strong>Kilómetros:</strong> {kilometers} km
      </Typography>
      
      {isHybrid ? (
        <>
          <Typography variant="body1" sx={{ color: 'text.secondary' }}>
            <strong>Tipo:</strong> Vehículo Híbrido
          </Typography>
          {vehicle.electric_range_km && (
            <Typography variant="body1" sx={{ color: 'text.secondary' }}>
              <strong>Rango eléctrico:</strong> {vehicle.electric_range_km} km
            </Typography>
          )}
          {vehicle.battery_capacity_kwh && (
            <Typography variant="body1" sx={{ color: 'text.secondary' }}>
              <strong>Capacidad de batería:</strong> {vehicle.battery_capacity_kwh} kWh
            </Typography>
          )}
          {vehicle.hybrid_efficiency_km_per_liter && (
            <Typography variant="body1" sx={{ color: 'text.secondary' }}>
              <strong>Eficiencia híbrida:</strong> {vehicle.hybrid_efficiency_km_per_liter} km/l
            </Typography>
          )}
          {initialBatteryPercentage !== null && initialBatteryPercentage !== undefined && (
            <Typography variant="body1" sx={{ color: 'text.secondary' }}>
              <strong>Batería inicial:</strong> {initialBatteryPercentage}%
            </Typography>
          )}
        </>
      ) : (
        <>
          <Typography variant="body1" sx={{ color: 'text.secondary' }}>
            <strong>Eficiencia:</strong> {vehicle.fuel_efficiency} km/l
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.secondary' }}>
            <strong>Tipo de combustible:</strong> {vehicle.fuel_type === 'regular' ? 'Gasolina Roja' : vehicle.fuel_type === 'premium' ? 'Gasolina Verde' : 'Diésel'}
          </Typography>
        </>
      )}

      {loading ? (
        <Box display="flex" alignItems="center" sx={{ mt: 1 }}>
          <CircularProgress size={20} sx={{ mr: 1 }} />
          <Typography variant="body2" color="text.secondary">
            Calculando costo...
          </Typography>
        </Box>
      ) : error ? (
        <Typography variant="body2" color="error" sx={{ mt: 1 }}>
          {error}
        </Typography>
      ) : hybridBreakdown ? (
        <Box sx={{ mt: 2 }}>
          {hybridBreakdown.exceedsElectricRange ? (
            <>
              <Typography variant="h6" sx={{ mb: 1, color: 'warning.main', fontWeight: 'bold' }}>
                ⚠️ Excede el rango eléctrico disponible
              </Typography>
              
              {/* Consumo Eléctrico */}
              <Paper sx={{ p: 2, mb: 2, backgroundColor: '#e3f2fd' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1, color: '#1976d2' }}>
                  Consumo Eléctrico
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  <strong>Kilómetros eléctricos:</strong> {hybridBreakdown.electricKm.toFixed(2)} km
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  <strong>kWh consumidos:</strong> {hybridBreakdown.electricKwh.toFixed(2)} kWh
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  <strong>Precio por kWh:</strong> {formatPrice(electricityPrice)}
                </Typography>
                <Typography variant="body1" sx={{ mt: 1, fontWeight: 'bold', color: '#1976d2' }}>
                  <strong>Costo eléctrico:</strong> {formatPrice(hybridBreakdown.electricCost)}
                </Typography>
              </Paper>

              {/* Consumo de Gasolina */}
              <Paper sx={{ p: 2, mb: 2, backgroundColor: '#fff3e0' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1, color: '#f57c00' }}>
                  Consumo de Gasolina
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  <strong>Kilómetros con gasolina:</strong> {hybridBreakdown.fuelKm.toFixed(2)} km
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  <strong>Litros necesarios:</strong> {hybridBreakdown.fuelLiters.toFixed(2)} L
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  <strong>Precio por litro:</strong> {formatPrice(fuelPrice)}
                </Typography>
                <Typography variant="body1" sx={{ mt: 1, fontWeight: 'bold', color: '#f57c00' }}>
                  <strong>Costo de gasolina:</strong> {formatPrice(hybridBreakdown.fuelCost)}
                </Typography>
              </Paper>

              {/* Total */}
              <Paper sx={{ p: 2, backgroundColor: '#e8f5e9', border: '2px solid #4caf50' }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#2e7d32', textAlign: 'center' }}>
                  <strong>Total a guardar:</strong> {formatPrice(hybridBreakdown.totalCost)}
                </Typography>
              </Paper>
            </>
          ) : (
            <>
              {/* Solo consumo eléctrico */}
              <Paper sx={{ p: 2, mb: 2, backgroundColor: '#e3f2fd' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1, color: '#1976d2' }}>
                  Consumo Eléctrico
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  <strong>Kilómetros:</strong> {hybridBreakdown.electricKm.toFixed(2)} km
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  <strong>kWh consumidos:</strong> {hybridBreakdown.electricKwh.toFixed(2)} kWh
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  <strong>Precio por kWh:</strong> {formatPrice(electricityPrice)}
                </Typography>
                <Typography variant="h6" sx={{ mt: 1, fontWeight: 'bold', color: '#1976d2' }}>
                  <strong>Costo total:</strong> {formatPrice(hybridBreakdown.totalCost)}
                </Typography>
              </Paper>
            </>
          )}
        </Box>
      ) : (
        <Typography variant="h6" sx={{ color: 'success.main', fontWeight: 'bold', mt: 1 }}>
          <strong>Costo estimado:</strong> {formatPrice(estimatedCost)}
        </Typography>
      )}
      
      <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
        * El costo se calcula usando los precios actuales del sistema
      </Typography>
    </>
  );
};

const FuelExpenses: React.FC = () => {
  const [completedEvents, setCompletedEvents] = useState<Event[]>([]);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [vehicleUsages, setVehicleUsages] = useState<EventVehicleUsage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [validationErrors, setValidationErrors] = useState<{[key: string]: string}>({});
  const [openErrorDialog, setOpenErrorDialog] = useState(false);
  const [errorMessages, setErrorMessages] = useState<string[]>([]);
  const [formData, setFormData] = useState({
    vehicle_id: '',
    kilometers: 0,
    initial_battery_percentage: null as number | null,
    notes: '',
  });

  // Estados para filtros
  const [searchName, setSearchName] = useState('');
  const [fuelStatusFilter, setFuelStatusFilter] = useState<'all' | 'with_fuel' | 'without_fuel'>('all');
  const [paymentStatusFilter, setPaymentStatusFilter] = useState<'all' | 'paid' | 'pending'>('all');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [events, vehiclesData, usages] = await Promise.all([
        eventService.getAll(),
        vehicleService.getAll(),
        eventVehicleUsageService.getAll()
      ]);
      
      // Filtrar solo eventos completados
      const completed = events.filter(event => event.status === 'completed');
      setCompletedEvents(completed);
      setVehicles(vehiclesData);
      setVehicleUsages(usages);
    } catch (err) {
      setError('Error al cargar los datos');
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const errors: {[key: string]: string} = {};
    const errorMessages: string[] = [];

    if (!formData.vehicle_id) {
      errors.vehicle_id = 'El vehículo es obligatorio';
      errorMessages.push('• El vehículo es obligatorio');
    }

    if (formData.kilometers <= 0) {
      errors.kilometers = 'Los kilómetros deben ser mayor a 0';
      errorMessages.push('• Los kilómetros deben ser mayor a 0');
    }

    return { errors, errorMessages };
  };

  const handleSave = async () => {
    try {
      setError(null);
      setValidationErrors({});

      const { errors, errorMessages } = validateForm();
      
      if (Object.keys(errors).length > 0) {
        setValidationErrors(errors);
        setErrorMessages(errorMessages);
        setOpenErrorDialog(true);
        return;
      }

      if (!selectedEvent) return;

      // Calcular el costo de gasolina usando el servicio del backend
      const fuelCostResponse = await vehicleService.calculateFuelCost(
        formData.vehicle_id, 
        formData.kilometers,
        formData.initial_battery_percentage
      );
      const fuelCost = fuelCostResponse.data?.fuel_cost || fuelCostResponse.fuel_cost || 0;

      const usageData = {
        event_id: selectedEvent.id,
        vehicle_id: formData.vehicle_id,
        kilometers: formData.kilometers,
        initial_battery_percentage: formData.initial_battery_percentage,
        fuel_cost: fuelCost,
        status: 'pending' as 'pending' | 'paid',
        notes: formData.notes,
      };

      await eventVehicleUsageService.create(usageData);
      fetchData();
      handleCloseDialog();
    } catch (err: any) {
      console.error('Error saving vehicle usage:', err);
      
      if (err.response?.data?.errors) {
        const backendErrors = err.response.data.errors;
        const errorMessages: string[] = [];
        const validationErrors: {[key: string]: string} = {};

        Object.keys(backendErrors).forEach(key => {
          const messages = backendErrors[key];
          if (Array.isArray(messages)) {
            errorMessages.push(...messages.map(msg => `• ${msg}`));
            validationErrors[key] = messages[0];
          }
        });

        setValidationErrors(validationErrors);
        setErrorMessages(errorMessages);
        setOpenErrorDialog(true);
      } else {
        setError('Error al guardar el viaje');
      }
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('¿Estás seguro de que quieres eliminar este viaje?')) {
      try {
        await eventVehicleUsageService.delete(id);
        fetchData();
      } catch (err) {
        setError('Error al eliminar el viaje');
        console.error('Error deleting vehicle usage:', err);
      }
    }
  };

  const handleMarkAsPaid = async (id: string) => {
    try {
      // Marcar como pagado en event-vehicle-usage
      await eventVehicleUsageService.markAsPaid(id);
      
      // Buscar el uso de vehículo para obtener información
      const vehicleUsage = vehicleUsages.find(usage => usage.id === id);
      if (vehicleUsage) {
        // Buscar el gasto relacionado en expenses por monto y fecha
        try {
          const allExpenses = await expenseService.getAll();
          console.log('Buscando gasto relacionado para vehicleUsage:', vehicleUsage);
          console.log('Total de gastos disponibles:', allExpenses.length);
          
          const relatedExpense = allExpenses.find(expense => {
            const usageAmount = typeof vehicleUsage.fuel_cost === 'string' ? parseFloat(vehicleUsage.fuel_cost) || 0 : vehicleUsage.fuel_cost || 0;
            const expenseAmount = typeof expense.amount === 'string' ? parseFloat(expense.amount) || 0 : expense.amount || 0;
            
            // Verificar si es un gasto de gasolina
            const isGasolineExpense = expense.category?.name?.toLowerCase().includes('gasolina') || 
                                     expense.concept?.toLowerCase().includes('gasolina');
            
            // Crear fechas para comparación
            const usageDate = new Date(vehicleUsage.created_at);
            const expenseDate = new Date(expense.expense_date);
            
            // Comparar solo el día, mes y año (ignorar hora)
            const usageDateOnly = new Date(usageDate.getFullYear(), usageDate.getMonth(), usageDate.getDate());
            const expenseDateOnly = new Date(expenseDate.getFullYear(), expenseDate.getMonth(), expenseDate.getDate());
            
            const amountMatch = Math.abs(usageAmount - expenseAmount) < 0.01;
            
            // Calcular diferencia en días
            const timeDiff = Math.abs(usageDateOnly.getTime() - expenseDateOnly.getTime());
            const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));
            
            // Permitir diferencia de hasta 30 días (más flexible)
            const dateMatch = daysDiff <= 30;
            
            console.log('Comparando gasto:', {
              usageAmount,
              expenseAmount,
              usageDate: usageDateOnly.toDateString(),
              expenseDate: expenseDateOnly.toDateString(),
              amountMatch,
              daysDiff,
              dateMatch,
              isGasolineExpense,
              usageId: vehicleUsage.id,
              expenseId: expense.id
            });
            
            return isGasolineExpense && amountMatch && dateMatch;
          });
          
          console.log('Gasto relacionado encontrado:', relatedExpense);
          
          if (relatedExpense) {
            // Marcar como pagado también en expenses
            await expenseService.markAsPaid(relatedExpense.id);
            console.log('Sincronización exitosa con expenses');
          } else {
            console.log('No se encontró gasto relacionado');
          }
        } catch (syncErr) {
          console.warn('Error al sincronizar con gastos regulares:', syncErr);
        }
      }
      
      fetchData();
    } catch (err) {
      setError('Error al marcar como pagado');
      console.error('Error marking as paid:', err);
    }
  };

  const handleOpenDialog = (event: Event) => {
    setSelectedEvent(event);
    setFormData({
      vehicle_id: '',
      kilometers: 0,
      initial_battery_percentage: null,
      notes: '',
    });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedEvent(null);
    setValidationErrors({});
    setErrorMessages([]);
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const getEventVehicleUsages = (eventId: string) => {
    return vehicleUsages.filter(usage => usage.event_id === eventId);
  };

  const getTotalFuelCost = (eventId: string) => {
    const usages = getEventVehicleUsages(eventId);
    return usages.reduce((total, usage) => {
      const cost = typeof usage.fuel_cost === 'string' ? parseFloat(usage.fuel_cost) || 0 : usage.fuel_cost || 0;
      return total + cost;
    }, 0);
  };

  const getVehicleInfo = (vehicleId: string) => {
    return vehicles.find(v => v.id === vehicleId);
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(price);
  };

  const formatDate = (dateString: string) => {
    // Crear fecha local sin problemas de zona horaria
    const [year, month, day] = dateString.split('T')[0].split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    return date.toLocaleDateString('es-MX');
  };

  // Función para calcular deudas por vehículo
  const getVehicleDebts = () => {
    const debts: { [vehicleId: string]: { vehicle: Vehicle; totalDebt: number; pendingUsages: number } } = {};
    
    vehicleUsages
      .filter(usage => usage.status === 'pending')
      .forEach(usage => {
        const vehicle = vehicles.find(v => v.id === usage.vehicle_id);
        if (vehicle) {
          if (!debts[usage.vehicle_id]) {
            debts[usage.vehicle_id] = {
              vehicle,
              totalDebt: 0,
              pendingUsages: 0
            };
          }
          const cost = typeof usage.fuel_cost === 'string' ? parseFloat(usage.fuel_cost) || 0 : usage.fuel_cost || 0;
          debts[usage.vehicle_id].totalDebt += cost;
          debts[usage.vehicle_id].pendingUsages += 1;
        }
      });
    
    return Object.values(debts);
  };

  // Función para filtrar eventos
  const getFilteredEvents = () => {
    return completedEvents.filter(event => {
      // Filtro por nombre
      if (searchName && !event.contact_name.toLowerCase().includes(searchName.toLowerCase())) {
        return false;
      }

      const eventUsages = getEventVehicleUsages(event.id);
      const hasFuel = eventUsages.length > 0;
      const hasPendingPayments = eventUsages.some(usage => usage.status === 'pending');

      // Filtro por estado de gasolina
      if (fuelStatusFilter === 'with_fuel' && !hasFuel) return false;
      if (fuelStatusFilter === 'without_fuel' && hasFuel) return false;

      // Filtro por estado de pago
      if (paymentStatusFilter === 'paid' && hasPendingPayments) return false;
      if (paymentStatusFilter === 'pending' && !hasPendingPayments) return false;

      return true;
    });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ color: divertilandiaColors.standard.primary, fontWeight: 'bold' }}>
            Gastos de Gasolina por Eventos
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Gestiona los gastos de gasolina de eventos completados
          </Typography>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Resumen de Deudas por Vehículo */}
      {(() => {
        const vehicleDebts = getVehicleDebts();
        if (vehicleDebts.length === 0) return null;
        
        return (
          <Alert 
            severity="warning" 
            sx={{ mb: 3 }}
            icon={<WarningIcon />}
          >
            <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold' }}>
              ⚠️ Deudas Pendientes por Vehículo
            </Typography>
            <Grid container spacing={2}>
              {vehicleDebts.map((debt) => (
                <Grid item xs={12} sm={6} md={4} key={debt.vehicle.id}>
                  <Card sx={{ backgroundColor: '#fff3cd', border: '1px solid #ffeaa7' }}>
                    <CardContent sx={{ p: 2 }}>
                      <Box display="flex" alignItems="center" sx={{ mb: 1 }}>
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            backgroundColor: debt.vehicle.color,
                            borderRadius: '50%',
                            mr: 1,
                          }}
                        />
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                          {debt.vehicle.name}
                        </Typography>
                      </Box>
                      <Typography variant="h6" sx={{ color: '#856404', fontWeight: 'bold' }}>
                        {formatPrice(debt.totalDebt)}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#856404' }}>
                        {debt.pendingUsages} viaje(s) pendiente(s)
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Alert>
        );
      })()}

      {/* Resumen */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
                <EventIcon sx={{ mr: 1, color: divertilandiaColors.rainbow.blue }} />
                <Typography variant="h6">Eventos Completados</Typography>
              </Box>
              <Typography variant="h4" sx={{ color: divertilandiaColors.rainbow.blue, fontWeight: 'bold' }}>
                {completedEvents.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
                <VehicleIcon sx={{ mr: 1, color: divertilandiaColors.rainbow.green }} />
                <Typography variant="h6">Viajes Registrados</Typography>
              </Box>
              <Typography variant="h4" sx={{ color: divertilandiaColors.rainbow.green, fontWeight: 'bold' }}>
                {vehicleUsages.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
                <MoneyIcon sx={{ mr: 1, color: divertilandiaColors.rainbow.orange }} />
                <Typography variant="h6">Total en Gasolina</Typography>
              </Box>
              <Typography variant="h4" sx={{ color: divertilandiaColors.rainbow.orange, fontWeight: 'bold' }}>
                {formatPrice(vehicleUsages.reduce((total, usage) => {
                  const cost = typeof usage.fuel_cost === 'string' ? parseFloat(usage.fuel_cost) || 0 : usage.fuel_cost || 0;
                  return total + cost;
                }, 0))}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filtros */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, color: divertilandiaColors.standard.primary, display: 'flex', alignItems: 'center' }}>
            <FilterIcon sx={{ mr: 1 }} />
            Filtros
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Buscar por nombre"
                value={searchName}
                onChange={(e) => setSearchName(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                placeholder="Ej: Juan Pérez"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Estado de Gasolina</InputLabel>
                <Select
                  value={fuelStatusFilter}
                  onChange={(e) => setFuelStatusFilter(e.target.value as any)}
                  label="Estado de Gasolina"
                >
                  <MenuItem value="all">Todos</MenuItem>
                  <MenuItem value="with_fuel">Con gasolina</MenuItem>
                  <MenuItem value="without_fuel">Sin gasolina</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Estado de Pago</InputLabel>
                <Select
                  value={paymentStatusFilter}
                  onChange={(e) => setPaymentStatusFilter(e.target.value as any)}
                  label="Estado de Pago"
                >
                  <MenuItem value="all">Todos</MenuItem>
                  <MenuItem value="paid">Pagados</MenuItem>
                  <MenuItem value="pending">Pendientes</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Lista de Eventos Completados */}
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Typography variant="h6" sx={{ mb: 2, color: divertilandiaColors.standard.primary }}>
            Eventos Completados ({getFilteredEvents().length})
          </Typography>
        </Grid>
        <Grid item xs={12}>
          {getFilteredEvents().length === 0 ? (
            <Card>
              <CardContent>
                <Typography variant="body1" color="text.secondary" textAlign="center">
                  {completedEvents.length === 0 
                    ? "No hay eventos completados para gestionar gastos de gasolina."
                    : "No se encontraron eventos que coincidan con los filtros aplicados."
                  }
                </Typography>
              </CardContent>
            </Card>
          ) : (
            getFilteredEvents().map((event) => {
              const eventUsages = getEventVehicleUsages(event.id);
              const totalCost = getTotalFuelCost(event.id);
              
              return (
                <Accordion key={event.id} sx={{ mb: 2 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box display="flex" alignItems="center" sx={{ width: '100%' }}>
                      <EventIcon sx={{ mr: 2, color: divertilandiaColors.rainbow.blue }} />
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="h6">
                          {event.contact_name} - {formatDate(event.event_date)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {event.address} • {eventUsages.length} vehículo(s) • {formatPrice(totalCost)}
                        </Typography>
                      </Box>
                      <Chip
                        label={(() => {
                          if (eventUsages.length === 0) return 'Sin gasolina';
                          const hasPending = eventUsages.some(usage => usage.status === 'pending');
                          if (hasPending) return 'Pendiente de pago';
                          return 'Con gasolina';
                        })()}
                        color={(() => {
                          if (eventUsages.length === 0) return 'default';
                          const hasPending = eventUsages.some(usage => usage.status === 'pending');
                          if (hasPending) return 'warning';
                          return 'success';
                        })()}
                        size="small"
                        sx={{ mr: 2 }}
                      />
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box sx={{ width: '100%' }}>
                      {/* Botón para agregar viaje */}
                      <Box sx={{ mb: 2 }}>
                        <Button
                          variant="outlined"
                          startIcon={<AddIcon />}
                          onClick={() => handleOpenDialog(event)}
                          sx={{ mb: 2 }}
                        >
                          Agregar Viaje
                        </Button>
                      </Box>

                      {/* Lista de viajes registrados */}
                      {eventUsages.length > 0 ? (
                        <TableContainer component={Paper}>
                          <Table>
                            <TableHead>
                              <TableRow>
                                <TableCell>Vehículo</TableCell>
                                <TableCell>Kilómetros</TableCell>
                                {eventUsages.some(u => {
                                  const v = getVehicleInfo(u.vehicle_id);
                                  return v?.vehicle_type === 'hybrid' && u.initial_battery_percentage !== null && u.initial_battery_percentage !== undefined;
                                }) && <TableCell>Batería Inicial</TableCell>}
                                <TableCell>Costo de Gasolina</TableCell>
                                <TableCell>Estado</TableCell>
                                <TableCell>Notas</TableCell>
                                <TableCell>Acciones</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {eventUsages.map((usage) => {
                                const vehicle = getVehicleInfo(usage.vehicle_id);
                                return (
                                  <TableRow key={usage.id}>
                                    <TableCell>
                                      <Box display="flex" alignItems="center">
                                        <VehicleIcon sx={{ mr: 1, color: vehicle?.color || '#95A5A6' }} />
                                        {vehicle?.name || 'Vehículo no encontrado'}
                                      </Box>
                                    </TableCell>
                                    <TableCell>
                                      <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                        {usage.kilometers} km
                                      </Typography>
                                    </TableCell>
                                    {eventUsages.some(u => {
                                      const v = getVehicleInfo(u.vehicle_id);
                                      return v?.vehicle_type === 'hybrid' && u.initial_battery_percentage !== null && u.initial_battery_percentage !== undefined;
                                    }) && (
                                      <TableCell>
                                        {vehicle?.vehicle_type === 'hybrid' && usage.initial_battery_percentage !== null && usage.initial_battery_percentage !== undefined ? (
                                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                            {usage.initial_battery_percentage}%
                                          </Typography>
                                        ) : (
                                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                            -
                                          </Typography>
                                        )}
                                      </TableCell>
                                    )}
                                    <TableCell>
                                      <Typography 
                                        variant="body1" 
                                        sx={{ 
                                          fontWeight: 'bold', 
                                          color: usage.status === 'pending' ? '#f57c00' : 'success.main' 
                                        }}
                                      >
                                        {formatPrice(usage.fuel_cost)}
                                      </Typography>
                                    </TableCell>
                                    <TableCell>
                                      <Chip
                                        label={usage.status === 'paid' ? 'Pagado' : 'Pendiente'}
                                        color={usage.status === 'paid' ? 'success' : 'warning'}
                                        size="small"
                                        icon={usage.status === 'paid' ? <CheckCircleIcon /> : <PendingIcon />}
                                      />
                                    </TableCell>
                                    <TableCell>
                                      {usage.notes ? (
                                        <Typography variant="body2" color="text.secondary">
                                          {usage.notes}
                                        </Typography>
                                      ) : (
                                        <Typography variant="body2" color="text.secondary">
                                          Sin notas
                                        </Typography>
                                      )}
                                    </TableCell>
                                    <TableCell>
                                      <Box display="flex" gap={1}>
                                        {usage.status === 'pending' && (
                                          <IconButton
                                            size="small"
                                            onClick={() => handleMarkAsPaid(usage.id)}
                                            sx={{ color: 'success.main' }}
                                            title="Marcar como pagado"
                                          >
                                            <CheckCircleIcon />
                                          </IconButton>
                                        )}
                                        <IconButton
                                          size="small"
                                          onClick={() => handleDelete(usage.id)}
                                          sx={{ color: 'error.main' }}
                                          title="Eliminar"
                                        >
                                          <DeleteIcon />
                                        </IconButton>
                                      </Box>
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      ) : (
                        <Paper sx={{ p: 3, textAlign: 'center' }}>
                          <Typography variant="body1" color="text.secondary">
                            No se han registrado viajes para este evento.
                          </Typography>
                        </Paper>
                      )}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              );
            })
          )}
        </Grid>
      </Grid>

      {/* Dialog para agregar viaje a evento */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          Agregar Viaje a Evento: {selectedEvent?.contact_name}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Evento: {selectedEvent?.contact_name} - {selectedEvent && formatDate(selectedEvent.event_date)}
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth required error={!!validationErrors.vehicle_id}>
                <InputLabel>Vehículo</InputLabel>
                <Select
                  value={formData.vehicle_id}
                  onChange={(e) => handleInputChange('vehicle_id', e.target.value)}
                  label="Vehículo"
                >
                  {vehicles.map((vehicle) => (
                    <MenuItem key={vehicle.id} value={vehicle.id}>
                      <Box display="flex" alignItems="center">
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            backgroundColor: vehicle.color,
                            borderRadius: '50%',
                            mr: 1,
                          }}
                        />
                        {vehicle.name} ({vehicle.fuel_type === 'regular' ? 'Gasolina Roja' : 'Gasolina Verde'})
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
                {validationErrors.vehicle_id && (
                  <Typography variant="body2" color="error" sx={{ mt: 0.5 }}>
                    {validationErrors.vehicle_id}
                  </Typography>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Kilómetros Recorridos"
                type="number"
                value={formData.kilometers}
                onChange={(e) => handleInputChange('kilometers', parseFloat(e.target.value) || 0)}
                inputProps={{ min: 0, step: 0.1 }}
                required
                error={!!validationErrors.kilometers}
                helperText={validationErrors.kilometers}
              />
            </Grid>

            {(() => {
              const selectedVehicle = vehicles.find(v => v.id === formData.vehicle_id);
              const isHybrid = selectedVehicle?.vehicle_type === 'hybrid';
              
              if (isHybrid) {
                return (
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Porcentaje de Batería Inicial"
                      type="number"
                      value={formData.initial_battery_percentage ?? ''}
                      onChange={(e) => {
                        const value = e.target.value === '' ? null : parseFloat(e.target.value);
                        handleInputChange('initial_battery_percentage', value);
                      }}
                      inputProps={{ min: 0, max: 100, step: 0.1 }}
                      helperText="Porcentaje de batería al inicio del viaje (0-100%). Déjalo vacío si estaba al 100%."
                      placeholder="Ej: 75 (o vacío para 100%)"
                    />
                  </Grid>
                );
              }
              return null;
            })()}

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notas (Opcional)"
                multiline
                rows={3}
                value={formData.notes}
                onChange={(e) => handleInputChange('notes', e.target.value)}
                placeholder="Ej: Ruta tomada, condiciones del tráfico, etc."
              />
            </Grid>

            {formData.vehicle_id && formData.kilometers > 0 && (
              <Grid item xs={12}>
                <Paper sx={{ p: 2, backgroundColor: '#f5f5f5' }}>
                  <Typography variant="h6" gutterBottom>
                    Resumen del Gasto
                  </Typography>
                  <FuelCostSummary 
                    vehicleId={formData.vehicle_id}
                    kilometers={formData.kilometers}
                    initialBatteryPercentage={formData.initial_battery_percentage}
                    vehicles={vehicles}
                    formatPrice={formatPrice}
                  />
                </Paper>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button onClick={handleSave} variant="contained">
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Errores de Validación */}
      <Dialog open={openErrorDialog} onClose={() => setOpenErrorDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ color: 'error.main', display: 'flex', alignItems: 'center' }}>
          <Alert severity="error" sx={{ mr: 2, p: 0, backgroundColor: 'transparent' }}>
            ⚠️
          </Alert>
          Error de Validación
        </DialogTitle>
        <DialogContent>
          <Typography variant="h6" gutterBottom>
            No se pudo guardar el viaje. Por favor corrige los siguientes errores:
          </Typography>
          <Box sx={{ mt: 2 }}>
            {errorMessages.map((message, index) => (
              <Typography key={index} variant="body1" sx={{ mb: 1, color: 'error.main' }}>
                {message}
              </Typography>
            ))}
          </Box>
          <Alert severity="info" sx={{ mt: 2 }}>
            Los campos marcados en rojo son obligatorios o tienen valores inválidos.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenErrorDialog(false)} variant="contained">
            Entendido
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FuelExpenses;
